/**
 * DriversManagementSection - Seção de gerenciamento de motoristas
 * Permite visualizar todos os motoristas e aprovar/rejeitar cadastros
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import {
    Loader2,
    CheckCircle,
    Clock,
    FileText,
    User,
    Car,
    Phone,
    Mail,
    MapPin,
    Eye,
    X,
    ChevronDown,
    ChevronUp,
    AlertCircle,
    RefreshCw,
    Search,
    ZoomIn,
    ZoomOut,
    Maximize2,
    AlertTriangle,
    XCircle,
    Edit3,
    Trash2
} from 'lucide-react';
import { toast } from 'sonner';

interface Driver {
    id: string;
    email: string;
    nome_completo: string;
    tel_whatsapp: number;
    cpf: string | null;
    rg: string | null;
    cnh_number: string | null;
    zip_code: string | null;
    street: string | null;
    number: string | null;
    complement: string | null;
    neighborhood: string | null;
    cidade: string | null;
    estado: string | null;
    vehicle_plate: string | null;
    vehicle_brand: string | null;
    vehicle_model: string | null;
    vehicle_color: string | null;
    vehicle_year: number | null;
    renavam: string | null;
    pix_key_type: string | null;
    pix_key: string | null;
    mobility_apps: string[] | null;
    operating_cities: string[] | null;
    selfie_url: string | null;
    app_profile_screenshot_url: string | null;
    cnh_photo_url: string | null;
    profile_status: 'pending_submission' | 'pending_review' | 'approved' | 'pending_correction' | 'rejected';
    correction_message: string | null;
    status: string;
    created_at: string;
    updated_at: string;
}

type ProfileStatus = 'pending_submission' | 'pending_review' | 'approved' | 'pending_correction' | 'rejected';

const statusLabels: Record<ProfileStatus, { label: string; color: string; icon: any }> = {
    pending_submission: { label: 'Pendente', color: 'bg-gray-100 text-gray-700', icon: FileText },
    pending_review: { label: 'Em Análise', color: 'bg-amber-100 text-amber-700', icon: Clock },
    pending_correction: { label: 'Correção', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
    approved: { label: 'Aprovado', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    rejected: { label: 'Rejeitado', color: 'bg-red-100 text-red-700', icon: XCircle }
};

// Componente de Visualizador de Imagem com Zoom
function ImageViewer({
    src,
    alt,
    onClose
}: {
    src: string;
    alt: string;
    onClose: () => void;
}) {
    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.5, 4));
    const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.5, 0.5));
    const handleReset = () => {
        setZoom(1);
        setPosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (zoom > 1) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging && zoom > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => setIsDragging(false);

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        if (e.deltaY < 0) {
            handleZoomIn();
        } else {
            handleZoomOut();
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/90 flex flex-col"
            onClick={onClose}
        >
            {/* Header com controles */}
            <div
                className="flex items-center justify-between p-4 bg-black/50"
                onClick={e => e.stopPropagation()}
            >
                <span className="text-white font-medium">{alt}</span>
                <div className="flex items-center gap-2">
                    {/* Controles de Zoom */}
                    <button
                        onClick={handleZoomOut}
                        className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        title="Diminuir zoom"
                    >
                        <ZoomOut className="w-5 h-5" />
                    </button>
                    <span className="text-white/80 text-sm min-w-[60px] text-center">
                        {Math.round(zoom * 100)}%
                    </span>
                    <button
                        onClick={handleZoomIn}
                        className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        title="Aumentar zoom"
                    >
                        <ZoomIn className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleReset}
                        className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors ml-2"
                        title="Resetar"
                    >
                        <Maximize2 className="w-5 h-5" />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors ml-4"
                        title="Fechar"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>
            </div>

            {/* Área da imagem */}
            <div
                className="flex-1 flex items-center justify-center overflow-hidden cursor-move"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onWheel={handleWheel}
                onClick={e => e.stopPropagation()}
            >
                <img
                    src={src}
                    alt={alt}
                    className="max-w-none transition-transform duration-200"
                    style={{
                        transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                        cursor: zoom > 1 ? 'grab' : 'default'
                    }}
                    draggable={false}
                />
            </div>

            {/* Dicas */}
            <div className="p-3 bg-black/50 text-center">
                <p className="text-white/60 text-xs">
                    Use a roda do mouse para zoom • Arraste para mover quando ampliado • Clique fora para fechar
                </p>
            </div>
        </div>
    );
}

// Componente de Thumbnail clicável
function DocumentThumbnail({
    src,
    label,
    onView
}: {
    src: string;
    label: string;
    onView: () => void;
}) {
    return (
        <div className="space-y-2 group">
            <p className="text-sm font-medium text-gray-700">{label}</p>
            <div className="relative">
                <img
                    src={src}
                    alt={label}
                    className="w-full h-40 object-cover rounded-lg border cursor-pointer transition-all group-hover:brightness-90"
                    onClick={onView}
                />
                <button
                    onClick={onView}
                    className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors rounded-lg"
                >
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                        <ZoomIn className="w-5 h-5 text-gray-700" />
                    </div>
                </button>
            </div>
        </div>
    );
}

export function DriversManagementSection() {
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState<string | null>(null);
    const [filter, setFilter] = useState<'all' | ProfileStatus>('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Estado para visualização de imagem
    const [viewingImage, setViewingImage] = useState<{ src: string; alt: string } | null>(null);

    // Estados para modais de correção e rejeição
    const [showCorrectionModal, setShowCorrectionModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [correctionMessage, setCorrectionMessage] = useState('');
    const [driverToAction, setDriverToAction] = useState<Driver | null>(null);

    // Carregar motoristas
    const loadDrivers = async () => {
        try {
            setIsLoading(true);
            const { data, error } = await supabase
                .from('drivers')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) {
                console.error('[DriversManagement] Erro ao carregar:', error);
                toast.error('Erro ao carregar motoristas');
                return;
            }

            setDrivers(data || []);
        } catch (error) {
            console.error('[DriversManagement] Erro:', error);
            toast.error('Erro ao carregar motoristas');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadDrivers();
    }, []);

    // Aprovar motorista
    const handleApprove = async (driverId: string) => {
        try {
            setIsUpdating(driverId);

            const { error } = await supabase
                .from('drivers')
                .update({ profile_status: 'approved' })
                .eq('id', driverId);

            if (error) {
                console.error('[DriversManagement] Erro ao aprovar:', error);
                toast.error('Erro ao aprovar motorista');
                return;
            }

            toast.success('Motorista aprovado com sucesso!');

            // Atualizar lista local
            setDrivers(prev => prev.map(d =>
                d.id === driverId ? { ...d, profile_status: 'approved' } : d
            ));

            // Fechar modal se estiver aberto
            if (selectedDriver?.id === driverId) {
                setSelectedDriver({ ...selectedDriver, profile_status: 'approved' });
            }
        } catch (error) {
            console.error('[DriversManagement] Erro:', error);
            toast.error('Erro ao aprovar motorista');
        } finally {
            setIsUpdating(null);
        }
    };

    // Solicitar correção
    const handleRequestCorrection = async () => {
        if (!driverToAction || !correctionMessage.trim()) {
            toast.error('Informe o que precisa ser corrigido');
            return;
        }

        try {
            setIsUpdating(driverToAction.id);

            const { error } = await supabase
                .from('drivers')
                .update({
                    profile_status: 'pending_correction',
                    correction_message: correctionMessage.trim()
                })
                .eq('id', driverToAction.id);

            if (error) {
                console.error('[DriversManagement] Erro ao solicitar correção:', error);
                toast.error('Erro ao solicitar correção');
                return;
            }

            toast.success('Solicitação de correção enviada!');

            setDrivers(prev => prev.map(d =>
                d.id === driverToAction.id ? { ...d, profile_status: 'pending_correction' as ProfileStatus, correction_message: correctionMessage.trim() } : d
            ));

            if (selectedDriver?.id === driverToAction.id) {
                setSelectedDriver({ ...selectedDriver, profile_status: 'pending_correction' as ProfileStatus, correction_message: correctionMessage.trim() });
            }

            setShowCorrectionModal(false);
            setCorrectionMessage('');
            setDriverToAction(null);
        } catch (error) {
            console.error('[DriversManagement] Erro:', error);
            toast.error('Erro ao solicitar correção');
        } finally {
            setIsUpdating(null);
        }
    };

    // Rejeitar permanentemente
    const handleRejectPermanently = async () => {
        if (!driverToAction) return;

        try {
            setIsUpdating(driverToAction.id);

            const { error } = await supabase
                .from('drivers')
                .update({ profile_status: 'rejected' })
                .eq('id', driverToAction.id);

            if (error) {
                console.error('[DriversManagement] Erro ao rejeitar:', error);
                toast.error('Erro ao rejeitar motorista');
                return;
            }

            toast.success('Motorista rejeitado');

            setDrivers(prev => prev.map(d =>
                d.id === driverToAction.id ? { ...d, profile_status: 'rejected' as ProfileStatus } : d
            ));

            if (selectedDriver?.id === driverToAction.id) {
                setSelectedDriver({ ...selectedDriver, profile_status: 'rejected' as ProfileStatus });
            }

            setShowRejectModal(false);
            setDriverToAction(null);
            setIsModalOpen(false);
        } catch (error) {
            console.error('[DriversManagement] Erro:', error);
            toast.error('Erro ao rejeitar motorista');
        } finally {
            setIsUpdating(null);
        }
    };

    // Revogar aprovação (voltar para pending_review)
    const handleRevoke = async (driverId: string) => {
        try {
            setIsUpdating(driverId);

            const { error } = await supabase
                .from('drivers')
                .update({ profile_status: 'pending_review', correction_message: null })
                .eq('id', driverId);

            if (error) {
                console.error('[DriversManagement] Erro ao revogar:', error);
                toast.error('Erro ao revogar aprovação');
                return;
            }

            toast.success('Aprovação revogada');

            setDrivers(prev => prev.map(d =>
                d.id === driverId ? { ...d, profile_status: 'pending_review' as ProfileStatus } : d
            ));

            if (selectedDriver?.id === driverId) {
                setSelectedDriver({ ...selectedDriver, profile_status: 'pending_review' as ProfileStatus });
            }
        } catch (error) {
            console.error('[DriversManagement] Erro:', error);
            toast.error('Erro ao revogar aprovação');
        } finally {
            setIsUpdating(null);
        }
    };

    // Filtrar motoristas por status e busca
    const filteredDrivers = drivers.filter(d => {
        // Filtro de status
        const statusMatch = filter === 'all' ? true : d.profile_status === filter;

        // Filtro de busca
        if (!searchTerm.trim()) return statusMatch;

        const search = searchTerm.toLowerCase();
        const searchMatch =
            d.nome_completo?.toLowerCase().includes(search) ||
            d.email?.toLowerCase().includes(search) ||
            d.cpf?.toLowerCase().includes(search) ||
            d.vehicle_plate?.toLowerCase().includes(search) ||
            d.tel_whatsapp?.toString().includes(search);

        return statusMatch && searchMatch;
    });

    // Contadores
    const counts = {
        all: drivers.length,
        pending_submission: drivers.filter(d => d.profile_status === 'pending_submission').length,
        pending_review: drivers.filter(d => d.profile_status === 'pending_review').length,
        pending_correction: drivers.filter(d => d.profile_status === 'pending_correction').length,
        approved: drivers.filter(d => d.profile_status === 'approved').length,
        rejected: drivers.filter(d => d.profile_status === 'rejected').length
    };

    // Formatar telefone
    const formatPhone = (phone: number | null) => {
        if (!phone) return '-';
        const str = phone.toString();
        if (str.length === 11) {
            return `(${str.slice(0, 2)}) ${str.slice(2, 7)}-${str.slice(7)}`;
        }
        return str;
    };

    // Formatar data
    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                <span className="ml-3 text-gray-600">Carregando motoristas...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* SearchBox */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por nome, email, CPF, placa ou telefone..."
                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all"
                />
                {searchTerm && (
                    <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Header com filtros */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                {/* Filtros */}
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'all'
                            ? 'bg-gray-800 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                    >
                        Todos ({counts.all})
                    </button>
                    <button
                        onClick={() => setFilter('pending_submission')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'pending_submission'
                            ? 'bg-gray-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                    >
                        Pendentes ({counts.pending_submission})
                    </button>
                    <button
                        onClick={() => setFilter('pending_review')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'pending_review'
                            ? 'bg-amber-600 text-white'
                            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                            }`}
                    >
                        Em Análise ({counts.pending_review})
                    </button>
                    <button
                        onClick={() => setFilter('pending_correction')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'pending_correction'
                            ? 'bg-orange-600 text-white'
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                            }`}
                    >
                        Correção ({counts.pending_correction})
                    </button>
                    <button
                        onClick={() => setFilter('approved')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'approved'
                            ? 'bg-emerald-600 text-white'
                            : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            }`}
                    >
                        Aprovados ({counts.approved})
                    </button>
                    <button
                        onClick={() => setFilter('rejected')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'rejected'
                            ? 'bg-red-600 text-white'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                    >
                        Rejeitados ({counts.rejected})
                    </button>
                </div>

                {/* Botão Atualizar */}
                <button
                    onClick={loadDrivers}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    <RefreshCw className="w-4 h-4" />
                    Atualizar
                </button>
            </div>

            {/* Resultado da busca */}
            {searchTerm && (
                <p className="text-sm text-gray-500">
                    {filteredDrivers.length} resultado(s) encontrado(s) para "{searchTerm}"
                </p>
            )}

            {/* Tabela de Motoristas */}
            {filteredDrivers.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <User className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">
                        {searchTerm ? 'Nenhum motorista encontrado para esta busca' : 'Nenhum motorista encontrado'}
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Motorista</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contato</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Veículo</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cadastro</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredDrivers.map((driver) => {
                                const StatusIcon = statusLabels[driver.profile_status].icon;
                                return (
                                    <tr key={driver.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-4">
                                            <div className="flex items-center gap-3">
                                                {driver.selfie_url ? (
                                                    <img
                                                        src={driver.selfie_url}
                                                        alt={driver.nome_completo}
                                                        className="w-10 h-10 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                        <User className="w-5 h-5 text-gray-500" />
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium text-gray-900">{driver.nome_completo}</p>
                                                    <p className="text-xs text-gray-500">{driver.cpf || 'CPF não informado'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="text-sm">
                                                <p className="text-gray-900">{driver.email}</p>
                                                <p className="text-gray-500">{formatPhone(driver.tel_whatsapp)}</p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="text-sm">
                                                {driver.vehicle_plate ? (
                                                    <>
                                                        <p className="font-medium text-gray-900">{driver.vehicle_plate}</p>
                                                        <p className="text-gray-500">{driver.vehicle_brand} {driver.vehicle_model}</p>
                                                    </>
                                                ) : (
                                                    <span className="text-gray-400">Não informado</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusLabels[driver.profile_status].color}`}>
                                                <StatusIcon className="w-3.5 h-3.5" />
                                                {statusLabels[driver.profile_status].label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {formatDate(driver.created_at)}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center justify-center gap-2">
                                                {/* Botão Ver Detalhes */}
                                                <button
                                                    onClick={() => {
                                                        setSelectedDriver(driver);
                                                        setIsModalOpen(true);
                                                    }}
                                                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                                    title="Ver detalhes"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>

                                                {/* Toggle de Aprovação */}
                                                {driver.profile_status === 'pending_review' && (
                                                    <button
                                                        onClick={() => handleApprove(driver.id)}
                                                        disabled={isUpdating === driver.id}
                                                        className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                                    >
                                                        {isUpdating === driver.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            'Aprovar'
                                                        )}
                                                    </button>
                                                )}

                                                {driver.profile_status === 'approved' && (
                                                    <span className="text-xs text-emerald-600 font-medium">✓ Aprovado</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de Detalhes */}
            {isModalOpen && selectedDriver && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
                        {/* Header do Modal */}
                        <div className="flex items-center justify-between p-6 border-b bg-gray-50">
                            <div className="flex items-center gap-4">
                                {selectedDriver.selfie_url ? (
                                    <img
                                        src={selectedDriver.selfie_url}
                                        alt={selectedDriver.nome_completo}
                                        className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
                                    />
                                ) : (
                                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                                        <User className="w-8 h-8 text-gray-500" />
                                    </div>
                                )}
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">{selectedDriver.nome_completo}</h2>
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusLabels[selectedDriver.profile_status].color}`}>
                                        {statusLabels[selectedDriver.profile_status].label}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Conteúdo do Modal */}
                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Dados Pessoais */}
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <User className="w-5 h-5 text-emerald-600" />
                                        Dados Pessoais
                                    </h3>
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                        <InfoRow label="Email" value={selectedDriver.email} />
                                        <InfoRow label="Telefone" value={formatPhone(selectedDriver.tel_whatsapp)} />
                                        <InfoRow label="CPF" value={selectedDriver.cpf} />
                                        <InfoRow label="RG" value={selectedDriver.rg} />
                                        <InfoRow label="CNH" value={selectedDriver.cnh_number} />
                                    </div>
                                </div>

                                {/* Endereço */}
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <MapPin className="w-5 h-5 text-emerald-600" />
                                        Endereço
                                    </h3>
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                        <InfoRow label="CEP" value={selectedDriver.zip_code} />
                                        <InfoRow label="Rua" value={selectedDriver.street} />
                                        <InfoRow label="Número" value={selectedDriver.number} />
                                        <InfoRow label="Complemento" value={selectedDriver.complement} />
                                        <InfoRow label="Bairro" value={selectedDriver.neighborhood} />
                                        <InfoRow label="Cidade/Estado" value={`${selectedDriver.cidade || '-'} / ${selectedDriver.estado || '-'}`} />
                                    </div>
                                </div>

                                {/* Veículo */}
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <Car className="w-5 h-5 text-emerald-600" />
                                        Veículo
                                    </h3>
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                        <InfoRow label="Placa" value={selectedDriver.vehicle_plate} />
                                        <InfoRow label="Marca" value={selectedDriver.vehicle_brand} />
                                        <InfoRow label="Modelo" value={selectedDriver.vehicle_model} />
                                        <InfoRow label="Cor" value={selectedDriver.vehicle_color} />
                                        <InfoRow label="Ano" value={selectedDriver.vehicle_year?.toString()} />
                                        <InfoRow label="Renavam" value={selectedDriver.renavam} />
                                    </div>
                                </div>

                                {/* PIX e Apps */}
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <Phone className="w-5 h-5 text-emerald-600" />
                                        PIX e Aplicativos
                                    </h3>
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                        <InfoRow label="Tipo PIX" value={selectedDriver.pix_key_type} />
                                        <InfoRow label="Chave PIX" value={selectedDriver.pix_key} />
                                        <InfoRow label="Apps" value={selectedDriver.mobility_apps?.join(', ')} />
                                        <InfoRow label="Cidades" value={selectedDriver.operating_cities?.join(', ')} />
                                    </div>
                                </div>
                            </div>

                            {/* Documentos/Fotos com visualizador */}
                            <div className="mt-6 space-y-4">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-emerald-600" />
                                    Documentos e Fotos
                                    <span className="text-xs font-normal text-gray-500 ml-2">
                                        (clique para ampliar)
                                    </span>
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {selectedDriver.selfie_url && (
                                        <DocumentThumbnail
                                            src={selectedDriver.selfie_url}
                                            label="Selfie"
                                            onView={() => setViewingImage({
                                                src: selectedDriver.selfie_url!,
                                                alt: 'Selfie do Motorista'
                                            })}
                                        />
                                    )}
                                    {selectedDriver.cnh_photo_url && (
                                        <DocumentThumbnail
                                            src={selectedDriver.cnh_photo_url}
                                            label="CNH"
                                            onView={() => setViewingImage({
                                                src: selectedDriver.cnh_photo_url!,
                                                alt: 'CNH do Motorista'
                                            })}
                                        />
                                    )}
                                    {selectedDriver.app_profile_screenshot_url && (
                                        <DocumentThumbnail
                                            src={selectedDriver.app_profile_screenshot_url}
                                            label="Print do App"
                                            onView={() => setViewingImage({
                                                src: selectedDriver.app_profile_screenshot_url!,
                                                alt: 'Print do Perfil no App'
                                            })}
                                        />
                                    )}
                                </div>
                                {!selectedDriver.selfie_url && !selectedDriver.cnh_photo_url && !selectedDriver.app_profile_screenshot_url && (
                                    <p className="text-gray-400 text-sm italic">Nenhum documento enviado</p>
                                )}
                            </div>
                        </div>

                        {/* Footer do Modal */}
                        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
                            <p className="text-sm text-gray-500">
                                Cadastrado em: {formatDate(selectedDriver.created_at)}
                            </p>
                            <div className="flex gap-3 flex-wrap justify-end">
                                {/* Status: Aprovado */}
                                {selectedDriver.profile_status === 'approved' && (
                                    <button
                                        onClick={() => handleRevoke(selectedDriver.id)}
                                        disabled={isUpdating === selectedDriver.id}
                                        className="px-4 py-2 border border-amber-300 text-amber-600 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50"
                                    >
                                        Revogar Aprovação
                                    </button>
                                )}

                                {/* Status: Em Análise */}
                                {selectedDriver.profile_status === 'pending_review' && (
                                    <>
                                        <button
                                            onClick={() => {
                                                setDriverToAction(selectedDriver);
                                                setShowRejectModal(true);
                                            }}
                                            disabled={isUpdating === selectedDriver.id}
                                            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            <XCircle className="w-4 h-4" />
                                            Rejeitar
                                        </button>
                                        <button
                                            onClick={() => {
                                                setDriverToAction(selectedDriver);
                                                setCorrectionMessage(selectedDriver.correction_message || '');
                                                setShowCorrectionModal(true);
                                            }}
                                            disabled={isUpdating === selectedDriver.id}
                                            className="px-4 py-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            <Edit3 className="w-4 h-4" />
                                            Solicitar Correção
                                        </button>
                                        <button
                                            onClick={() => handleApprove(selectedDriver.id)}
                                            disabled={isUpdating === selectedDriver.id}
                                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {isUpdating === selectedDriver.id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <CheckCircle className="w-4 h-4" />
                                            )}
                                            Aprovar
                                        </button>
                                    </>
                                )}

                                {/* Status: Aguardando Correção */}
                                {selectedDriver.profile_status === 'pending_correction' && (
                                    <span className="text-sm text-orange-600 italic flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        Aguardando correção do motorista
                                    </span>
                                )}

                                {/* Status: Pendente */}
                                {selectedDriver.profile_status === 'pending_submission' && (
                                    <span className="text-sm text-gray-500 italic">
                                        Motorista ainda não enviou o formulário
                                    </span>
                                )}

                                {/* Status: Rejeitado */}
                                {selectedDriver.profile_status === 'rejected' && (
                                    <span className="text-sm text-red-600 italic flex items-center gap-2">
                                        <XCircle className="w-4 h-4" />
                                        Cadastro rejeitado permanentemente
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Solicitação de Correção */}
            {showCorrectionModal && driverToAction && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-6 border-b bg-emerald-50">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <Edit3 className="w-5 h-5 text-emerald-700" />
                                Solicitar Correção
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Informe ao motorista o que precisa ser corrigido
                            </p>
                        </div>
                        <div className="p-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mensagem de correção *
                            </label>
                            <textarea
                                value={correctionMessage}
                                onChange={(e) => setCorrectionMessage(e.target.value)}
                                placeholder="Ex: A foto da CNH está ilegível. Por favor, envie uma nova foto com boa iluminação."
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 resize-none"
                                rows={4}
                            />
                            <p className="text-xs text-gray-500 mt-2">
                                Esta mensagem será exibida ao motorista na tela de correção.
                            </p>
                        </div>
                        <div className="flex gap-3 p-6 border-t bg-gray-50">
                            <button
                                onClick={() => {
                                    setShowCorrectionModal(false);
                                    setCorrectionMessage('');
                                    setDriverToAction(null);
                                }}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleRequestCorrection}
                                disabled={!correctionMessage.trim() || isUpdating === driverToAction.id}
                                className="flex-1 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isUpdating === driverToAction.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Edit3 className="w-4 h-4" />
                                )}
                                Enviar Solicitação
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Rejeição Permanente */}
            {showRejectModal && driverToAction && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-6 border-b bg-red-50">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <XCircle className="w-5 h-5 text-red-600" />
                                Rejeitar Cadastro
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Esta ação é permanente e não pode ser desfeita
                            </p>
                        </div>
                        <div className="p-6">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                <p className="text-sm text-red-800">
                                    <strong>Atenção:</strong> Ao rejeitar este cadastro:
                                </p>
                                <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                                    <li>O motorista verá uma mensagem de não aprovação</li>
                                    <li>Os dados serão marcados para exclusão</li>
                                    <li>Esta ação não pode ser revertida</li>
                                </ul>
                            </div>
                            <p className="text-sm text-gray-600">
                                Deseja realmente rejeitar o cadastro de <strong>{driverToAction.nome_completo}</strong>?
                            </p>
                        </div>
                        <div className="flex gap-3 p-6 border-t bg-gray-50">
                            <button
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setDriverToAction(null);
                                }}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleRejectPermanently}
                                disabled={isUpdating === driverToAction.id}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isUpdating === driverToAction.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <XCircle className="w-4 h-4" />
                                )}
                                Rejeitar Permanentemente
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Visualizador de Imagem com Zoom */}
            {viewingImage && (
                <ImageViewer
                    src={viewingImage.src}
                    alt={viewingImage.alt}
                    onClose={() => setViewingImage(null)}
                />
            )}
        </div>
    );
}

// Componente auxiliar para exibir informações
function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
    return (
        <div className="flex justify-between">
            <span className="text-sm text-gray-500">{label}:</span>
            <span className="text-sm font-medium text-gray-900">{value || '-'}</span>
        </div>
    );
}

export default DriversManagementSection;
